import { FC, useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { btnSizes, btnTypes } from '@r1-frontend/ui-react/components/buttons/baseButton';
import { Cost } from '@r1-frontend/ui-react/experimental/purchase/cost';
import { getPluralize } from '@r1-frontend/shared/helpers/pluralize';
import { channelPluralize } from '@r1-frontend/shared/helpers/pluralize/predifined';

import InfoSection from '~/src/features/payments-abn/components/InfoSection';
import { getPeriodData } from '~/src/features/payments-abn/helpers/getPeriodData';
import { pushDataLayer } from '~/src/features/payments-abn/helpers/pushDataLayer';
import { ECategoryId, EServiceId, IPeriodCharges } from '~/src/features/payments-abn/models';
import { monthString } from '~/src/helpers/date';
import { TState } from '~/src/store';

import ChargeItem from './chargeItem';

import * as ST from './styled';

const channelsText = getPluralize(channelPluralize);

const PaymentCheck: FC = () => {
    const currentMonth = monthString(new Date());

    const { clientProducts } = useSelector((state: TState) => state.auth);

    const [charges, setCharges] = useState<IPeriodCharges>();
    const [isOpen, setIsOpen] = useState(false);
    const [isShowingAll, setIsShowingAll] = useState(false);

    const fetchPeriodCharges = useCallback(async() => {
        const { PeriodCharges } = await getPeriodData(new Date());

        const _charges = { ...PeriodCharges } as IPeriodCharges;
        _charges.categories?.forEach(category => {
            const { CategoryId, subcategories } = category;

            if (CategoryId === ECategoryId.subscriptionFee) {
                category.CategoryName += ` за тариф ${subcategories[0].PlanName}`;

                subcategories.forEach(subcategory => {
                    const subcategoryService = clientProducts?.services.find(service => service.planName === subcategory.PlanName);

                    if (subcategoryService) {
                        const { productId, specification, planName } = subcategoryService;
                        switch (productId) {
                            case EServiceId.INTERNET:
                                subcategory.SubcategoryName += `: ${specification} Мбит/сек`;
                                break;
                            case EServiceId.TELEPHONY:
                                subcategory.SubcategoryName += `: ${planName} тариф`;
                                break;
                            case EServiceId.KTV:
                            case EServiceId.CKTV:
                            case EServiceId.DOMRUTV:
                                subcategory.SubcategoryName += `: ${specification} ${channelsText(specification)}`;
                                break;
                        }
                    }
                });
            }

            if (CategoryId === ECategoryId.equipment) {
                subcategories.forEach(subcategory => {
                    if (subcategory.equipments?.length) {
                        subcategory.SubcategoryName = subcategory.equipments[0];
                    }
                });
            }
        });

        setCharges(_charges);
    }, [clientProducts]);

    useEffect(() => {
        fetchPeriodCharges();
    }, []);

    const onToggleOpen = useCallback(() => {
        setIsOpen(!isOpen);
        pushDataLayer(isOpen ? 'roll up receipt' : 'expand receipt', 'click');
    }, [isOpen]);

    const { PeriodTotalSum = 0, categories = [] } = charges || {};

    return (charges
        ? <InfoSection
            title={`Чек за ${currentMonth}`}
            isOpen={isOpen}
            onToggle={onToggleOpen}
        >
            <InfoSection.Content isAccordion>
                <ST.ChargesList>
                    {categories.map((category, index) => {
                        if (!isShowingAll && index > 1) {
                            return null;
                        }
                        return <ChargeItem
                            key={category.CategoryId}
                            category={category}
                        />;
                    })}
                    {categories.length > 2 && (
                        <ST.ShowMoreBtn
                            type={btnTypes.SECONDARY}
                            size={btnSizes.SMALL}
                            text={isShowingAll ? 'Свернуть' : 'Показать больше '}
                            iconsSprite={'/static/images/icons/payment-sprite.svg'}
                            symbolId={'show-more'}
                            isRotatedIcon={!isShowingAll}
                            onClick={() => setIsShowingAll(!isShowingAll)}
                        />
                    )}
                </ST.ChargesList>
                <ST.TotalPayment>
                    <ST.MonthDetail>
                        <ST.TotalRow>
                            <ST.PaymentName>Ежемесячный платёж:</ST.PaymentName>
                            <ST.PaymentSum>
                                <Cost isMonthly>{Math.abs(PeriodTotalSum)}</Cost>
                            </ST.PaymentSum>
                        </ST.TotalRow>
                    </ST.MonthDetail>
                    <ST.TotalMonth>
                        <ST.TotalText>Итого за&nbsp;{currentMonth}:</ST.TotalText>
                        <ST.TotalText>
                            <Cost>{Math.abs(PeriodTotalSum)}</Cost>
                        </ST.TotalText>
                    </ST.TotalMonth>
                </ST.TotalPayment>
            </InfoSection.Content>
        </InfoSection>
        : null
    );
};

export default PaymentCheck;
